import { useAnalytics } from '@repo/internal-tools-analytics/src/AnalyticsProvider.tsx'
import { useRouterState } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'

/**
 * Track an analytics page view on every navigation (including initial load).
 *
 * - `screenName`: leaf route template (`match.fullPath`, e.g. `/tasks/$taskId`)
 * - `properties.path`: resolved pathname (e.g. `/tasks/123`)
 * - `properties.url`: full URL (e.g. `https://local-ccc.earnin.net/.../tasks/123`)
 */
export const useTrackAnalyticsPageViews = () => {
  const { pageView } = useAnalytics()

  const lastTrackedKeyRef = useRef<string | undefined>(undefined)

  // IMPORTANT: Select from a single router-state snapshot to avoid mismatched pairs
  // (e.g. old unresolvedPath + new resolvedPath) during navigation.
  const { resolvedPath, unresolvedPath } = useRouterState({
    select: (state) => {
      const leafMatch = state.matches.at(-1)

      return {
        // The resolved pathname (e.g. `/user/123`)
        // Prefer the leaf match's `pathname` so `screenName` (leaf `fullPath`) and `path`
        // remain consistent during phased navigation updates.
        resolvedPath:
          leafMatch?.pathname ??
          state.resolvedLocation?.pathname ??
          state.location.pathname,
        // The unresolved pathname (e.g. `/user/$userId`)
        unresolvedPath: leafMatch?.fullPath as string | undefined,
      }
    },
  })

  // Keyed on pathname so dynamic routes (e.g. /tasks/:id) still emit when params change.
  useEffect(() => {
    if (!unresolvedPath) return

    const url = typeof window === 'undefined' ? undefined : window.location.href

    // Dedupe repeated emissions of the exact same screen+path within a single mount.
    // This is mainly to guard against React StrictMode dev behavior where effects can run twice
    // with identical dependencies. Note: this does NOT prevent multiple pageViews during a single
    // navigation if the router produces different (screenName,path) pairs across phased updates.
    const trackKey = `${unresolvedPath}::${resolvedPath}`
    if (lastTrackedKeyRef.current === trackKey) return
    lastTrackedKeyRef.current = trackKey

    // Use unresolved path as the screenName
    pageView(unresolvedPath, {
      path: resolvedPath,
      ...(url ? { url } : {}),
    })
  }, [pageView, resolvedPath, unresolvedPath])
}
